import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiClock, FiUsers, FiUtensils, FiCheck, FiPhone } = FiIcons;

function Restaurant() {
  const specialties = [
    "Red Country Rice (Heenati) & White Samba",
    "Freshwater Fish Ambul Thiyal",
    "Young Jackfruit (Polos) Curry",
    "Gotukola & Mukunuwenna Mallum",
    "Curd & Kithul Treacle Dessert",
    "Fresh Lake Fish & Traditional Sides"
  ];

  const features = [
    { icon: FiUsers, title: "Group Capacity", desc: "Seats 50+ guests comfortably" },
    { icon: FiClock, title: "Quick Service", desc: "Buffet ready in 15 minutes" },
    { icon: FiUtensils, title: "Authentic Cuisine", desc: "Traditional Sabaragamuwa dishes" },
    { icon: FiCheck, title: "Clean Facilities", desc: "Western-standard restrooms" }
  ];

  return (
    <section id="restaurant" className="section-padding">
      <div className="container">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Taste of <span className="text-gradient">Panamure</span>
          </h2>
          <p className="text-xl text-white/80 max-w-3xl mx-auto">
            Savor authentic Sabaragamuwa cuisine featuring fresh local ingredients and traditional recipes 
            passed down through generations. Perfect for individual diners and large tour groups.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12">
          <motion.div
            className="space-y-8"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="glass-card p-8">
              <h3 className="text-3xl font-bold mb-6">Our Signature Buffet</h3>
              <p className="text-white/90 mb-6">
                Experience the authentic flavors of Sabaragamuwa with our carefully curated buffet 
                featuring locally sourced ingredients and traditional cooking methods.
              </p>
              
              <div className="space-y-3">
                <h4 className="text-xl font-semibold text-blue-300 mb-4">Today's Menu Highlights:</h4>
                {specialties.map((item, index) => (
                  <motion.div
                    key={index}
                    className="flex items-start gap-3 p-3 glass-card rounded-lg"
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <SafeIcon icon={FiCheck} className="text-green-400 mt-1 flex-shrink-0" />
                    <span className="text-white/90">{item}</span>
                  </motion.div>
                ))}
              </div>
            </div>

            <motion.div
              className="glass-card p-6"
              whileHover={{ scale: 1.02 }}
            >
              <h4 className="text-xl font-semibold mb-4">Pricing</h4>
              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span>Adult Buffet</span>
                  <span className="text-blue-300 font-semibold">LKR 1,800</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Child Buffet (5-12 years)</span>
                  <span className="text-blue-300 font-semibold">LKR 900</span>
                </div>
                <div className="flex justify-between items-center">
                  <span>Group Discounts Available</span>
                  <span className="text-green-400 font-semibold">Call for rates</span>
                </div>
              </div>
            </motion.div>
          </motion.div>

          <motion.div
            className="space-y-6"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="grid grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="glass-card p-6 text-center"
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="w-12 h-12 mx-auto mb-3 glass-card rounded-full flex items-center justify-center">
                    <SafeIcon icon={feature.icon} className="text-blue-300" />
                  </div>
                  <h4 className="font-semibold mb-2">{feature.title}</h4>
                  <p className="text-sm text-white/80">{feature.desc}</p>
                </motion.div>
              ))}
            </div>

            <div className="glass-card p-8">
              <h4 className="text-2xl font-semibold mb-6">Perfect for Tour Groups</h4>
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-400" />
                  <span>Capacity for 50+ guests</span>
                </div>
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-400" />
                  <span>Dedicated driver lounge & complimentary meals</span>
                </div>
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-400" />
                  <span>Ample bus parking space</span>
                </div>
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-400" />
                  <span>Clean, SLTDA-certified facilities</span>
                </div>
                <div className="flex items-center gap-3">
                  <SafeIcon icon={FiCheck} className="text-green-400" />
                  <span>Quick service - buffet ready in 15 minutes</span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <a 
                href="tel:076-8727112" 
                className="btn-glass w-full justify-center pulse-glow"
              >
                <SafeIcon icon={FiPhone} />
                Reserve Your Table - Call Now
              </a>
              <a 
                href="https://wa.me/94768727112?text=Hi, I'd like to book a group lunch at Kumarana Hotel"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-glass w-full justify-center"
              >
                Book Group Lunch via WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

export default Restaurant;